import { ShieldCheck, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import type { AnomalyReport } from "@/api/queries";
import { EmptyState } from "@/components/common/EmptyState";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { formatDuration, formatPeriod, formatShortDate } from "@/lib/format";

/** Kinds that mean someone imitated work rather than simply lost the network. */
const serious = new Set(["synthetic_input", "slot_rewrite", "static_screen", "monotonous_input"]);

/**
 * What the server noticed about tracked time, by person and day. The time is
 * still counted: every row is a place to look, not a verdict.
 */
export function AnomalyReportView({ data, lang }: { data: AnomalyReport; lang: string }) {
  const { t } = useTranslation();
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-4">
        <CardTitle>{t("reports.anomalies.title")}</CardTitle>
        <CardDescription>
          {t("reports.anomalies.hint", { period: formatPeriod(data.from, data.to, lang) })}
        </CardDescription>
      </CardHeader>
      {data.rows.length === 0 ? (
        <EmptyState icon={ShieldCheck} title={t("reports.anomalies.empty")} />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>{t("reports.anomalies.date")}</TH>
              <TH>{t("reports.groups.user")}</TH>
              <TH>{t("reports.anomalies.reasons")}</TH>
              <TH className="text-right">{t("reports.anomalies.tracked")}</TH>
              <TH className="text-right">{t("reports.anomalies.findings")}</TH>
            </tr>
          </THead>
          <tbody>
            {data.rows.map((r) => (
              <TR key={`${r.user.id}-${r.date}`}>
                <TD className="whitespace-nowrap">
                  <Link to={`/activity/${r.user.id}?date=${r.date}`} className="font-medium hover:underline">
                    {formatShortDate(r.date, lang)}
                  </Link>
                </TD>
                <TD>
                  <span className="flex min-w-0 items-center gap-2">
                    <UserAvatar name={r.user.full_name} className="size-6 text-[10px]" />
                    <span className="truncate font-medium">{r.user.full_name}</span>
                  </span>
                </TD>
                <TD>
                  <span className="flex flex-wrap gap-1.5">
                    {r.kinds.map((k) => (
                      <span
                        key={k.kind}
                        className={
                          serious.has(k.kind)
                            ? "inline-flex items-center gap-1 rounded-full bg-danger-soft px-2 py-0.5 text-xs font-medium whitespace-nowrap text-danger"
                            : "inline-flex items-center gap-1 rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium whitespace-nowrap text-muted"
                        }
                      >
                        {serious.has(k.kind) && <TriangleAlert className="size-3" aria-hidden="true" />}
                        {t(`anomalies.kinds.${k.kind}`)}
                        {k.count > 1 && <span className="tabular-nums">· {k.count}</span>}
                      </span>
                    ))}
                  </span>
                </TD>
                <TD className="text-right text-muted tabular-nums">
                  {formatDuration(r.tracked_seconds, lang)}
                </TD>
                <TD className="text-right font-medium tabular-nums">{r.total}</TD>
              </TR>
            ))}
          </tbody>
          <tfoot className="border-t bg-surface-muted/60 font-semibold">
            <tr>
              <td className="px-5 py-2.5" colSpan={4}>
                {t("reports.totalPeople", { count: new Set(data.rows.map((r) => r.user.id)).size })}
              </td>
              <td className="px-5 py-2.5 text-right tabular-nums">{data.total}</td>
            </tr>
          </tfoot>
        </Table>
      )}
    </Card>
  );
}
